import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import ClientDataDialog from "../components/dialogs/ClientDataDialog";
import UserSettingsDialog from "../components/dialogs/UserSettingsDialog";
import DiscardDataDialog from "../components/dialogs/DiscardDataDialog";

interface DialogContextType {
  isClientDataDialogOpened: boolean,
  openClientDataDialog: () => void;
  closeClientDataDialog: () => void;

  isUserSettingsDialogOpened: boolean,
  openUserSettingsDialog: () => void;
  closeUserSettingsDialog: () => void;

  isDiscardDataDialogOpened: boolean,
  openDiscardDataDialog: () => void;
  closeDiscardDataDialog: () => void;

  isAddDialogOpen: boolean;
  openAddDialog: () => void;
  closeAddDialog: () => void;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

interface DialogProviderProps {
  children: ReactNode;
}

export const DialogProvider: React.FC<DialogProviderProps> = ({ children }) => {
  // Add Source Dialog state
  const [isAddDialogOpen, setAddDialogOpen] = useState(false);

  // Client data
  const [isClientDataDialogOpened, setClientDataDialogOpened] = useState(false);

  // User Settings
  const [isUserSettingsDialogOpened, setUserSettingsDialogOpened] = useState(false);

  // Discard data
  const [isDiscardDataDialogOpened, setDiscardDataDialogOpened] = useState(false);

  // COM-637 task 5 fix: every open/close action below is wrapped in
  // `useCallback` (empty deps — each only calls a `useState` setter, which
  // React guarantees is itself stable) and the context `value` in `useMemo`.
  // Before this fix, a plain arrow function per action meant a NEW function
  // reference on every DialogProvider render, and a fresh `value` object
  // every time. `OnboardingGate` (mounted inside DialogProvider, see
  // App.tsx) depends on `openClientDataDialog` in its effect's dependency
  // array, so closing ClientDataDialog on "Finish Onboarding" - which
  // re-renders DialogProvider - re-ran that effect purely because the
  // function identity changed, not because anything OnboardingGate actually
  // cares about did. Since the locally-held project's `companyType` had not
  // yet been echoed back non-empty by the (async) `setProjectData` response
  // at that point, `shouldPromptForClientData` was still true, so the
  // effect called `openClientDataDialog()` again immediately - reopening
  // the wizard on the same step before its exit transition (and the
  // discovery question that transition triggers) could ever complete.
  // Caught by e2e/ui/onboarding/onboarding-wizard.spec.ts's "walking all
  // four steps ... reaches Finish Onboarding" test, which failed with the
  // wizard reopened on step 4 and the discovery question never appearing.
  const openClientDataDialog = useCallback(() => setClientDataDialogOpened(true), []);
  const closeClientDataDialog = useCallback(() => setClientDataDialogOpened(false), []);

  const openUserSettingsDialog = useCallback(() => setUserSettingsDialogOpened(true), []);
  const closeUserSettingsDialog = useCallback(() => setUserSettingsDialogOpened(false), []);

  const openDiscardDataDialog = useCallback(() => setDiscardDataDialogOpened(true), []);
  const closeDiscardDataDialog = useCallback(() => setDiscardDataDialogOpened(false), []);

  const openAddDialog = useCallback(() => setAddDialogOpen(true), []);
  const closeAddDialog = useCallback(() => setAddDialogOpen(false), []);

  const value: DialogContextType = useMemo(() => ({
    isClientDataDialogOpened,
    openClientDataDialog,
    closeClientDataDialog,
    isUserSettingsDialogOpened,
    openUserSettingsDialog,
    closeUserSettingsDialog,
    isDiscardDataDialogOpened,
    openDiscardDataDialog,
    closeDiscardDataDialog,
    isAddDialogOpen,
    openAddDialog,
    closeAddDialog,
  }), [
    isClientDataDialogOpened,
    openClientDataDialog,
    closeClientDataDialog,
    isUserSettingsDialogOpened,
    openUserSettingsDialog,
    closeUserSettingsDialog,
    isDiscardDataDialogOpened,
    openDiscardDataDialog,
    closeDiscardDataDialog,
    isAddDialogOpen,
    openAddDialog,
    closeAddDialog,
  ]);

  return (
    <DialogContext.Provider value={value}>
      {children}

      <ClientDataDialog
          open={isClientDataDialogOpened}
          onClose={closeClientDataDialog}
      />

      <UserSettingsDialog
          open={isUserSettingsDialogOpened}
          onClose={closeUserSettingsDialog}
      />

      <DiscardDataDialog
          open={isDiscardDataDialogOpened}
          onClose={closeDiscardDataDialog}
      />
    </DialogContext.Provider>
  );
};

export const useDialog = (): DialogContextType => {
  const context = useContext(DialogContext);
  if (context === undefined) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
}; 